<script>
import {defineComponent} from 'vue'
import AspectImg from "@/components/image/AspectImg.vue";
import {mapActions, mapGetters, mapState} from "vuex";
import {PRODUCT_REGION_CLICK, PRODUCT_REGION_CLICK_VALID} from "@/components/block/BlockEvents";
import Lytics from "@/classes/Lytics";

const firedImpressions = new Set();

export default defineComponent({
	name: "WishListProduct",
	components: {AspectImg},
	props: {
		product: {
			type: Object
		}
	},
	computed: {
		...mapGetters(['content', 'language', 'productsBySKU', 'storeId']),
		...mapState(['currentPageNumber']),
		...mapGetters('landingPage', ['currentFlyer']),
		lang() {
			return this.content?.feature?.wishlist || {};
		},
		image() {
			return this.product?.images?.[0];
		},
		imageUrl() {
			return this.image?.url;
		},
		imageAlt() {
			return this.image?.altText;
		},
		percSavings() {
			return Math.round(this.product?.percentageSavings || 0);
		},
		currentPrice() {
			return this.language === 'fr'? String(this.product?.currentPrice)?.replace('.', ',') : this.product?.currentPrice;
		},
		originalPrice() {
			return this.language === 'fr'? String(this.product?.originalPrice)?.replace('.', ',') : this.product?.originalPrice;
		},
		pdpProd() {
			const prod =  this.productsBySKU[this.product.code];
			if(!prod) return null;
			return {product: prod, sku:this.product.code, blockData: prod.blockData, storeId: this.storeId, currentFlyer: this.currentFlyer, currentPageNumber: this.currentPageNumber, language: this.language};
		},
		ariaLabel(){
			return `${this.product?.name}, $${this.currentPrice}, ${this.lang.save} ${this.percSavings}% `;
		}
	},
	data() {
		return {
			observer: null,
			wasIntersecting: false,
			leaveViewportTimer: null
		}
	},
	mounted() {
		this.setupIntersectionObserver();
	},
	beforeDestroy() {
		if (this.observer) {
			this.observer.disconnect();
		}
		if (this.leaveViewportTimer) {
			clearTimeout(this.leaveViewportTimer);
		}
	},
	methods: {
		...mapActions('events', ['triggerEvent']),
		setupIntersectionObserver() {
			const options = {
				root: null,
				rootMargin: '0px',
				threshold: 0.1
			};
			
			this.observer = new IntersectionObserver((entries) => {
				entries.forEach(entry => {
					const productCode = this.product.code;
					
					if (entry.isIntersecting && !this.wasIntersecting && !firedImpressions.has(productCode)) {
						if (this.leaveViewportTimer) {
							clearTimeout(this.leaveViewportTimer);
							this.leaveViewportTimer = null;
						}
						
						firedImpressions.add(productCode);
						this.wasIntersecting = true;
						Lytics.TriggerDynamicPromoImpression({
              id: this.product.code,
              name: this.product.name,
              img: this.imageUrl,
              link_name: "",
              type: "wishlist",
              sub_type: null
            });
					} else if (!entry.isIntersecting && this.wasIntersecting) {
						if (this.leaveViewportTimer) {
							clearTimeout(this.leaveViewportTimer);
						}
						
						this.leaveViewportTimer = setTimeout(() => {
							this.wasIntersecting = false;
							firedImpressions.delete(productCode);
							this.leaveViewportTimer = null;
						}, 500);
					}
				});
			}, options);
			
			this.observer.observe(this.$el);
		},
		onClick(){
			Lytics.TriggerDynamicPromoClick({
				id: this.product.code,
				name: this.product.name,
				img: this.imageUrl,
				link_name: "",
				type: "wishlist",
				sub_type: null
			});
			
			if(this.pdpProd) {
				this.triggerEvent({event:PRODUCT_REGION_CLICK, data : this.pdpProd});
				this.triggerEvent({event:PRODUCT_REGION_CLICK_VALID, data : this.pdpProd});
			}
		}
	}
})
</script>

<template>
	<div class="wish-list-product" :aria-label="ariaLabel" @click="onClick" tabindex="0">
		<img :alt="imageAlt" :src="imageUrl" class="prod-image"/>
		<div class="details">
			<div class="now red">{{ lang.now }}</div>
			<div class="pricing">
				<div class="current-price red">
					<div class="prefix">$</div>
					<div>{{ currentPrice }}</div>
				</div>
				<div class="original-price"><span>$</span><span>{{ originalPrice }}</span></div>

			</div>
			<div class="perc-save">{{ lang.save }} {{ percSavings }}%</div>
			<div class="prod-name" :title="product.name">{{ product.name }}</div>

		</div>


	</div>
</template>

<style lang="scss" scoped>
.wish-list-product {
  background-color: #FFF;
  font-size: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: CT Eastman Condensed;
  font-weight: 700;
  gap: 1em;
  cursor: pointer;


  .red {
	color: #D81E05;
  }

  .prod-image {
	aspect-ratio: 1/1;
	width: calc(100% - 20px);
	object-fit: contain;
	margin-top: 10px;
  }

  .details {
	font-family: Lato, Arial, sans-serif;
	line-height: 1.5em;
	width: calc(100% - 20px);
	margin-bottom: 10px;
	.now {
	  font-size: 1.16em;
	  font-family: CTEastmanCondensed, sans-serif
	}

	.pricing {
	  font-family: CTEastmanCompressed, sans-serif;
	  font-weight: bold;
	  display: flex;
	  align-items: flex-end;
	  gap: 0.3em;

	  .current-price {
		font-size: 2.66em;
		display: flex;
		align-items: flex-start;
		line-height: normal;
	    [mode=mobile] & {
		  font-size: 1.66em;
	    }

		.prefix {
		  font-size: 0.5em;
		  line-height: normal;
		}

	    [lang="fr"] & {
	      direction: rtl;
	    }

	  }

	  .original-price {
		color: #737373;
		font-size: 1.16em;
		text-decoration: line-through;
		font-weight: 400;
	    display: flex;
		[lang="fr"] & {
		  direction: rtl;
		  gap: 0.1em;
		}
	  }
	}

	.prod-name {
	  [flyer-orientation="horizontal"] & {
	    overflow: hidden;
		text-overflow: ellipsis;
		-webkit-line-clamp: 4;
		-webkit-box-orient: vertical;
		display: -webkit-box;
	  }
	  font-size: 1em;
	}

	.perc-save {
	  font-size: 1em;
	  color: #737373;
	  font-weight: 400;
	}
  }


}
</style>
